import {
    createAsyncThunk,
    createSlice,
    type PayloadAction,
} from "@reduxjs/toolkit";

import { fetchNextPageFromApi } from "~api/client/fetchNextPageFromApi";
import type { RootState } from "~redux/store";
import type {
    HydratedCurationModule,
    HydratedModulesById,
    PaginatedModule,
} from "~types/types";
import getMergedAndDedupedItems from "~utils/getMergedAndDedupedItems";

const initialState: HydratedModulesById = {};

/**
 * @todo: [ENG4AUD-784]remove modulesById from redux
 */
interface PaginationProps {
    module: PaginatedModule;
    pageId: string;
}

export const fetchPagination = async ({ module, pageId }: PaginationProps) => {
    const paginatedModules = await fetchNextPageFromApi({
        id: module.id,
        pageId,
        offset: module.nextItemOffset,
        type: module.type,
        variation: module.variation,
    });

    return paginatedModules;
};

export const mergeWithPaginatedModules = createAsyncThunk<
    HydratedModulesById,
    { module: PaginatedModule; paginatedModules: HydratedModulesById },
    {
        state: RootState;
    }
>(
    "modulesById/modulesPaginationFetched",
    (
        {
            module,
            paginatedModules,
        }: { module: PaginatedModule; paginatedModules: HydratedModulesById },
        thunkAPI
    ) => {
        const state: HydratedModulesById = thunkAPI.getState().modulesById;

        const newModules = Object.entries(paginatedModules).reduce(
            (acc, [id, paginatedModule]) => {
                if (!paginatedModule) return acc;

                const newModule: HydratedCurationModule = {
                    ...paginatedModule,
                    items: getMergedAndDedupedItems(
                        module.items,
                        paginatedModule?.items || []
                    ),
                };

                return {
                    ...acc,
                    [id]: newModule,
                };
            },
            state
        );

        return newModules;
    }
);

const modulesByIdSlice = createSlice({
    name: "modulesById",
    initialState,
    reducers: {
        modulesByIdFetched(state, action: PayloadAction<HydratedModulesById>) {
            return {
                ...state,
                ...action.payload,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            mergeWithPaginatedModules.fulfilled,
            (state, action) => {
                return {
                    ...state,
                    ...action.payload,
                };
            }
        );
    },
});

export const { modulesByIdFetched } = modulesByIdSlice.actions;

export default modulesByIdSlice.reducer;
